<template>
  <div class="window" :class="{ expanded }">
    <div class="heading" :style="`background: ${config.primary_color || '#9747ff'}`">
      <div class="btn-group">
        <button @click="closeChatbot">
          <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41Z"
              fill="#fff" />
          </svg>
        </button>
        <button @click="toggleExpanded">
          <svg v-if="!expanded" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 14H5v5h5v-2H7v-3Zm-2-4h2V7h3V5H5v5Zm12 7h-3v2h5v-5h-2v3ZM14 5v2h3v3h2V5h-5Z" fill="#fff" />
          </svg>
          <svg v-else width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 16h3v3h2v-5H5v2Zm3-8H5v2h5V5H8v3Zm6 11h2v-3h3v-2h-5v5Zm2-11V5h-2v5h5V8h-3Z" fill="#fff" />
          </svg>
        </button>
      </div>
      <h2>
        <svg class="icon" width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd"
            d="m7.558 11.48 1.519-4.553h.932l1.52 4.553 4.557 1.518v.931l-4.557 1.518L10.009 20h-.932l-1.52-4.553L3 13.929v-.931l4.558-1.518ZM16.157 5.344 16.939 3h.932l.782 2.344L21 6.126v.931l-2.347.781-.782 2.345h-.932l-.782-2.345-2.347-.781v-.931l2.347-.782Z"
            fill="currentColor" />
        </svg>
        <span>{{ content.chatbot["window-title"] }}</span>
      </h2>
      <p class="subheading">{{ content.chatbot["window-subtitle"] }}</p>
    </div>
    <div v-if="messages.length === 0" class="suggested-queries">
      <button v-for="suggestion in suggestions" :key="suggestion" :disabled="resultsLoading" @click="queryAISearch(suggestion)"
        :style="`background: ${config.secondary_color + '15' || '#8725BE'}`">
        {{ suggestion }}
      </button>
    </div>
    <div ref="messageList" class="message-list">
      <p class="disclaimer">{{ disclaimerText }}</p>
      <ChatbotMessage v-for="({ display_content, display_products, role }, index) in messages" :key="index" :role="role"
        :content="display_content" :products="display_products" />
      <ChatbotMessage v-if="resultsLoading" role="assistant" :content="responseContent"
        :products="relevantProductsFull" latest />
    </div>
    <div class="query" :style="`background: ${config.secondary_color || '#8725BE'}`">
      <svg class="icon" width="16" height="16" xmlns="http://www.w3.org/2000/svg" :style="`color: ${config.primary_color || '#9747ff'}`">
        <path
          d="M13.333 1.333H2.673c-.733 0-1.333.6-1.333 1.334l-.007 12L4 12h9.333c.734 0 1.334-.6 1.334-1.333v-8c0-.734-.6-1.334-1.334-1.334ZM8 4c.733 0 1.333.6 1.333 1.333 0 .734-.6 1.334-1.333 1.334s-1.333-.6-1.333-1.334C6.667 4.6 7.267 4 8 4Zm2.667 5.333H5.333v-.38c0-.54.32-1.02.814-1.233a4.634 4.634 0 0 1 3.706 0c.494.213.814.693.814 1.233v.38Z"
          fill="currentColor" />
      </svg>
      <label class="sr-only" for="query">AI Chatbot Query</label>
      <input :disabled="resultsLoading" ref="input" type="text" name="query" id="query" :placeholder="content.chatbot['placeholder']"
        v-model="query" @keyup.enter="handleSubmit" />
      <button v-if="query" class="clear-button" @click="handleClear">
        <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="m12.667 4.273-.94-.94L8 7.06 4.274 3.333l-.94.94L7.06 8l-3.726 3.727.94.94L8 8.94l3.727 3.727.94-.94L8.94 8l3.727-3.727Z"
            fill="#000" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import ChatbotMessage from './ChatbotMessage.vue';
import moment from 'moment';

export default {
  components: {
    ChatbotMessage
  },
  data() {
    return {
      query: '',
      expanded: false
    };
  },
  computed: {
    ...mapGetters(['content']),
    ...mapGetters('config', ['features']),
    ...mapState('search', ['messages', 'resultsLoading', 'aiSuggestions']),
    ...mapGetters('search', ['relevantProductsFull', 'responseContent']),
    ...mapGetters('landingPage', ['currentFlyer']),

    config() {
      return this.features.chatbot || {};
    },

    suggestions() {
      return this.aiSuggestions || this.config.suggestions || [];
    },

    disclaimerText() {
      if (!this.currentFlyer || !this.currentFlyer.startDate || !this.currentFlyer.endDate) {
        return '';
      }
      const startDate = moment.utc(this.currentFlyer.startDate);
      const endDate = moment.utc(this.currentFlyer.endDate);
      const formatDate = (date) => {
        return date.format('MM/DD/YY');
      };
      return `${this.content.chatbot['disclaimer-1']}${formatDate(startDate)}-${formatDate(endDate)}${this.content.chatbot['disclaimer-2']}`;
    }
  },
  methods: {
    ...mapActions('search', ['queryAISearch']),

    handleSubmit() {
      if (this.query) {
        this.queryAISearch(this.query);
        this.query = '';
      }
    },

    handleClear(e) {
      e.stopPropagation();
      this.query = '';
      this.$refs.input.focus();
    },

    scrollToBottom() {
      this.$refs.messageList.scrollTop = this.$refs.messageList.scrollHeight;
    },

    closeChatbot() {
      this.$emit('close');
    },

    toggleExpanded() {
      this.expanded = !this.expanded;
    }
  },
  watch: {
    messages() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    relevantProductsFull() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    },
    responseContent() {
      this.$nextTick(() => {
        this.scrollToBottom();
      });
    }
  },
  mounted() {
    this.$refs.input.focus();
    this.scrollToBottom();
  }
};
</script>

<style scoped lang="scss">
@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeOutDown {
  0% {
    opacity: 1;
    transform: translateY(0);
  }

  100% {
    opacity: 0;
    transform: translateY(20px);
  }
}

.window {
  width: 390px;
  background-color: #fff;
  min-height: 60vh;
  max-height: 80vh;
  border-radius: 4px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.25);
  animation: fadeInUp 0.5s ease-out;
  display: flex;
  flex-direction: column;

  &.expanded {
    width: 640px;
    min-height: 80vh;
  }
}

@media screen and (max-width: 768px) {
  .window {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 0;
    min-height: 100vh;
    width: 100vw;
  }
}

@media screen and (max-height: 800px) {
  .window {
    min-height: 85vh;
    max-height: 85vh;
  }
}

.window.v-leave-active {
  animation: fadeOutDown 0.5s ease-out;
}

h2 {
  display: flex;
  gap: 8px; 
  margin-bottom: 8px;
  font-size: 18px;
}

.heading {
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
  font-size: 16px;
  color: #fff;
  padding: 16px;
  position: relative;

  .btn-group {
    display: flex;
    position: absolute;
    top: 4px;
    right: 4px;

    button {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      outline: none;
      width: 24px;
      height: 24px;

      &:hover, &:focus-visible {
        background: rgba(255, 255, 255, 0.2);
      }
    }
  }
}

.suggested-queries {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 18px 16px;
  border-bottom: 1px solid rgba(231, 231, 231, 1);

  button {
    border: none;
    border-radius: 24px;
    color: #414141;
    outline: none;

    &:hover, &:focus-visible {
      outline: 1px solid rgba(0, 0, 0, 0.4);
    }
  }
}

.disclaimer {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin: 12px 8px;
 
  a {
    color: rgba(0, 0, 0, 0.6);
    text-decoration: underline;
  }
}

.message-list {
  padding: 12px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.query {
  position: relative;
  padding: 12px;
  margin-top: auto;
  background: #9747ff;
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;

  .icon {
    position: absolute;
    top: 24px;
    left: 20px;
  }

  input {
    border: 1px solid rgba(214, 214, 214, 1);
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.25) inset;
    border-radius: 4px;
    width: 100%;
    font-size: 14px;
    padding: 10px 32px 10px 32px;
    transition: border 0.3s;


    &:focus-visible {
      outline: none;
      border: 1px solid rgba(235, 0, 255, 1);
    }

    &:disabled {
      background: rgba(255, 255, 255, 0.9);
      color: rgba(0, 0, 0, 0.3);
    }
  }

  .clear-button {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    outline: none;
  }
}
</style>
